// Contract Items API Service - Hierarchical Support
// Date: 28/07/2025
// Purpose: API service for hierarchical contract items management

import api from "services/api";

export const contractItemsApi = {
  // Lấy danh sách hạng mục phân cấp theo hợp đồng
  getItemsHierarchicalByContractId: async (contractId) => {
    try {
      const response = await api.get(
        `/contract-items/by-contract/${contractId}/hierarchical`
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching hierarchical contract items:", error);
      throw error;
    }
  },

  // Tạo hạng mục mới với auto-generate ItemCode
  createItemWithAutoCode: async (itemData) => {
    try {
      const response = await api.post(
        "/contract-items/with-auto-code",
        itemData
      );
      return response.data;
    } catch (error) {
      console.error("Error creating contract item with auto code:", error);
      throw error;
    }
  },

  // Tạo ItemCode tự động
  importItems: async (contractId, items, targetParentItemId = null) => {
    try {
      const response = await api.post("/contract-items/import", {
        contractId: parseInt(contractId),
        targetParentItemId,
        items,
      });
      return response.data;
    } catch (error) {
      console.error("Error importing contract items:", error);
      throw error;
    }
  },

  generateItemCode: async (contractId, parentItemId = null) => {
    try {
      const params = parentItemId ? `?parentItemId=${parentItemId}` : "";
      const response = await api.get(
        `/contract-items/generate-code/${contractId}${params}`
      );
      return response.data;
    } catch (error) {
      console.error("Error generating item code:", error);
      throw error;
    }
  },

  // Lấy danh mục đơn vị tính trong hợp đồng
  getUnitsInContract: async () => {
    try {
      const response = await api.get("/UnitInContract", {
        params: {
          pageSize: 1000,
          pageNumber: 1,
          sortColumn: "Name",
          sortDirection: "ASC",
        },
      });
      return response.data?.data ?? response.data ?? [];
    } catch (error) {
      console.error("Error fetching units in contract:", error);
      return [];
    }
  },

  // Lấy thông tin hạng mục theo ID
  getItemById: async (itemId) => {
    try {
      const response = await api.get(`/contract-items/${itemId}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching contract item:", error);
      throw error;
    }
  },

  // Cập nhật hạng mục
  updateItem: async (itemId, itemData) => {
    try {
      const response = await api.put(`/contract-items/${itemId}`, itemData);
      return response.data;
    } catch (error) {
      console.error("Error updating contract item:", error);
      throw error;
    }
  },

  // Cập nhật trạng thái hạng mục
  updateItemStatus: async (itemId, statusData) => {
    try {
      const response = await api.patch(
        `/contract-items/${itemId}/status`,
        statusData
      );
      return response.data;
    } catch (error) {
      console.error("Error updating contract item status:", error);
      throw error;
    }
  },

  // Cập nhật phần trăm hoàn thành
  updateCompletionPercentage: async (itemId, completionPercentage) => {
    try {
      const response = await api.patch(
        `/contract-items/${itemId}/completion-percentage`,
        { completionPercentage }
      );
      return response.data;
    } catch (error) {
      console.error("Error updating completion percentage:", error);
      throw error;
    }
  },

  // Chuyển đổi thành hạng mục cha
  convertToParent: async (itemId) => {
    try {
      const response = await api.put(
        `/contract-items/${itemId}/convert-to-parent`
      );
      return response.data;
    } catch (error) {
      console.error("Error converting to parent:", error);
      // Nếu có response từ server với thông báo lỗi, trả về thông báo đó
      if (error.response && error.response.data) {
        return error.response.data;
      }
      throw error;
    }
  },

  // Xóa hạng mục
  deleteItem: async (itemId) => {
    try {
      const response = await api.delete(`/contract-items/${itemId}`);
      return response.data;
    } catch (error) {
      console.error("Error deleting contract item:", error);
      // Nếu có response từ server với thông báo lỗi, trả về thông báo đó
      if (error.response && error.response.data) {
        return error.response.data;
      }
      throw error;
    }
  },

  // Lấy danh sách hạng mục theo hợp đồng (với phân trang)
  getItemsByContractId: async (contractId, params = {}) => {
    try {
      const {
        pageSize = 10,
        pageNumber = 1,
        searchTerm = "",
        sortColumn = "ItemCode",
        sortDirection = "ASC",
      } = params;

      const queryParams = new URLSearchParams({
        pageSize: pageSize.toString(),
        pageNumber: pageNumber.toString(),
        sortColumn,
        sortDirection,
      });

      if (searchTerm) {
        queryParams.append("searchTerm", searchTerm);
      }

      const response = await api.get(
        `/contract-items/by-contract/${contractId}?${queryParams}`
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching contract items with pagination:", error);
      throw error;
    }
  },
};

// Utility functions for hierarchical data processing
export const contractItemsUtils = {
  // Tính tổng giá trị cho parent items
  calculateParentAmount: (item, allItems) => {
    if (!item.children || item.children.length === 0) {
      return item.contractAmount || 0;
    }

    return item.children.reduce((total, child) => {
      return total + contractItemsUtils.calculateParentAmount(child, allItems);
    }, 0);
  },

  // Flatten hierarchical data thành flat array
  flattenHierarchy: (items) => {
    const result = [];

    const flatten = (itemList, level = 0) => {
      itemList.forEach((item) => {
        result.push({ ...item, level });
        if (item.children && item.children.length > 0) {
          flatten(item.children, level + 1);
        }
      });
    };

    flatten(items);
    return result;
  },

  // Tìm item theo ID trong cấu trúc phân cấp
  findItemInHierarchy: (items, itemId) => {
    for (const item of items) {
      if (item.itemId === itemId) {
        return item;
      }
      if (item.children && item.children.length > 0) {
        const found = contractItemsUtils.findItemInHierarchy(
          item.children,
          itemId
        );
        if (found) return found;
      }
    }
    return null;
  },

  // Cập nhật item trong cấu trúc phân cấp
  updateItemInHierarchy: (items, itemId, updatedData) => {
    return items.map((item) => {
      if (item.itemId === itemId) {
        return { ...item, ...updatedData };
      }
      if (item.children && item.children.length > 0) {
        return {
          ...item,
          children: contractItemsUtils.updateItemInHierarchy(
            item.children,
            itemId,
            updatedData
          ),
        };
      }
      return item;
    });
  },

  // Xóa item khỏi cấu trúc phân cấp
  removeItemFromHierarchy: (items, itemId) => {
    return items.filter((item) => {
      if (item.itemId === itemId) {
        return false;
      }
      if (item.children && item.children.length > 0) {
        item.children = contractItemsUtils.removeItemFromHierarchy(
          item.children,
          itemId
        );
      }
      return true;
    });
  },

  // Validate cấu trúc phân cấp (không quá 3 cấp)
  validateHierarchyDepth: (items, maxDepth = 3) => {
    const checkDepth = (itemList, currentDepth = 0) => {
      if (currentDepth >= maxDepth) {
        return false;
      }

      for (const item of itemList) {
        if (item.children && item.children.length > 0) {
          if (!checkDepth(item.children, currentDepth + 1)) {
            return false;
          }
        }
      }
      return true;
    };

    return checkDepth(items);
  },

  // Format số tiền
  // formatCurrency: (amount) => {
  //   if (!amount) return "0 VND";
  //   return new Intl.NumberFormat("vi-VN", {
  //     style: "currency",
  //     currency: "VND",
  //   }).format(amount);
  // },

  // Format mã hạng mục để hiển thị indentation
  formatItemCodeWithIndent: (itemCode, level) => {
    const indent = "  ".repeat(level); // 2 spaces per level
    return `${indent}${itemCode}`;
  },
};

export default contractItemsApi;
